import React, { useEffect, useMemo, useState } from "react";
import { Badge, Card, message, Pagination, Space, Table, Tag } from "antd";
import type { ColumnsType } from "antd/es/table";
import moment from "moment";
import { EyeOutlined, YoutubeOutlined } from "@ant-design/icons";
import { Link } from "@tanstack/react-router";
import VideoDetailDrawer from "../component/videoDetailDrawer";
import { IVideo, Status } from "../interfaces";
import HistoryFilter from "../component/history/HistoryFilter";
import LinkItem from "../component/history/LinkItem";
import { statusColor } from "../utils/constants";
import useList from "../hooks/useList";
import AppHeader from "../layouts/header";
import { adminApi } from "../api";
import SyncButton from "./syncButton";
export type scheduleType = "title" | "schedule_on" | "course_id";
export interface ICourse {
  id: string;
  title: string;
  lessons: string[];
}

function List({ type }: any) {
  const [loading, setLoading] = useState(false);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [viewableVideo, setViewableVideo] = useState<IVideo | null>(null);
  const { query, list, isLoading, updateList, updateQuery, onDelete } =
    useList(type);

  const [videoUrls, setVideoUrls] = useState<any>();

  const ids = useMemo(() => {
    return (
      list?.videos
        ?.map((i) => i.vid)
        .filter((v: any) => /^\d+$/.test(v))
        .map((v) => Number(v)) ?? []
    );
  }, [list]);

  const token = localStorage.getItem("AUTH_TOKEN");

  const getVideoUrls = async () => {
    try {
      setLoading(true);
      const payload = {
        videoIds: ids,
      };
      const { data } = await adminApi.post(
        "https://admin2.khanglobalstudies.com/studio/videos/v2/get-video-urls",
        payload,
        {},
      );
      setVideoUrls(data?.data);
      setLoading(false);
    } catch (error) {
      setLoading(false);
      message.error(error.message);
    }
  };

  useEffect(() => {
    if (ids.length > 0) {
      getVideoUrls();
    }
  }, [query.status, ids]);

  const columns: ColumnsType<IVideo> = [
    {
      title: "S No.",
      dataIndex: "sno",
      key: "sno",
      render: (_, __, index) => {
        return index + 1 + query.pageSize * (query.page - 1);
      },
      width: "5%",
      align: "center",
      responsive: ["lg", "md"],
    },
    {
      title: "Video Id",
      dataIndex: "vid",
      key: "vid",
      render: (vid, __, index) => vid,
      width: "8%",
      align: "center",
      // responsive: ["lg", "md"],
    },
    {
      title: "Course Id",
      dataIndex: "course_id",
      key: "course_id",
      render: (courseId) => courseId,
      width: "8%",
      align: "center",
    },
    {
      title: "Title",
      dataIndex: ["title", "url"],
      key: "title",
      render: (_, data) => (
        <Link
          target="_blank"
          rel="noopener noreferrer"
          to={`/studio/$slug`}
          params={{ slug: data._id }}
        >
          {data.title}
        </Link>
      ),
      align: "center",
    },
    {
      title: "Date",
      dataIndex: "schedule_at",
      key: "schedule_at",
      render: (schedule_at) => (
        <Badge>{moment(schedule_at).format("DD-MM-YY HH:MM")}</Badge>
      ),
      align: "center",
      responsive: ["lg"],
    },
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      render: (status: Status) => (
        <Tag color={statusColor[status]} key={status}>
          {status}
        </Tag>
      ),
      align: "center",
    },
    {
      title: "Video Link",
      dataIndex: "url",
      key: "url",
      render: LinkItem,
      responsive: ["lg"],
    },
    {
      title: "Views",
      dataIndex: "views",
      key: "views",
      render: (url, data) => {
        if (["yt", "ytst"].includes(data.videoType))
          return (
            <YoutubeOutlined
              style={{ color: "red", fontSize: 25 }}
              color="red"
            />
          );
        return (
          <div
            style={{
              ...(data.status !== "schedule" && {
                display: "flex",
                gap: "15px",
              }),
            }}
          >
            {data.status !== "schedule" && (
              <SyncButton videoUrls={videoUrls} data={data} />
            )}

            <EyeOutlined
              onClick={() => {
                setViewableVideo(data);
                setDrawerOpen(true);
              }}
            />
          </div>
        );
      },
      align: "center",
    },
  ];

  // const updateVideo = (video: IVideo) => {
  //   if (video._id === viewableVideo?._id) setViewableVideo(video);
  //   updateList(video);
  // };

  // useEffect(() => {
  //   socket.taskListUpdated(updateVideo);
  //   socket.liveRenderedList(updateVideo);
  //   socket.liveEndedList(updateVideo);
  // }, [drawerOpen, updateVideo]);

  return (
    <>
      <AppHeader />
      <Card type="inner" title="Logs & Activities">
        <HistoryFilter
          query={query}
          setQuery={(query) => updateQuery({ ...query, page: 1 })}
          type={type}
        />
        <Table
          columns={columns}
          dataSource={list?.videos}
          pagination={false}
          loading={isLoading || loading}
          scroll={{ x: 800 }}
        />
        <Space>
          <Pagination
            hideOnSinglePage={true}
            style={{ margin: "10px 0" }}
            defaultCurrent={query.page}
            current={query.page}
            total={list?.count}
            onChange={(page, pageSize) => updateQuery({ page, pageSize })}
            defaultPageSize={query?.pageSize ?? 10}
          />
        </Space>
      </Card>
      {viewableVideo && (
        <VideoDetailDrawer
          onDelete={(video) => onDelete(video)}
          video={viewableVideo!}
          drawerOpen={drawerOpen}
          setDrawerOpen={setDrawerOpen}
        />
      )}
    </>
  );
}

export default List;
